import {
  GambitAttendanceStatus,
  type BaseAttendanceDto,
  type BaseGambitDto,
  type GambitFormData,
  type GambitRecurrenceOptions,
  type SparkGambitOptions,
} from '@/core/data/gambit/gambit.interface';
import type { GambitRepository } from '@/core/data/gambit/gambit.repository';
import type { GambitInvitationType } from '@/core/data/gambit/gambit.type';
import type { GeoCoords } from '@/core/data/location/location.interface';
import type { LiveGambitDto, OutgoingInvitationDto, RespondGambitDto } from '@/core/network/api/gambit/gambit.api';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { generalError } from '@/core/network/http/httpError';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { GambitSearchResultDto } from '@/views/composables/models/components/GambitSearchResultDto';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import type { GambitForm } from '@/views/composables/models/form.interface';

export class GambitService {
  private readonly repository: GambitRepository;

  constructor(repository: GambitRepository) {
    this.repository = repository;
  }

  private buildWallDateTime(date: string, time: string): string {
    return `${date.substring(0, 10)}T${time}:00`;
  }

  async findGambitResults(
    locationIds: string[],
    start: string | Date,
    end: string | Date,
  ): Promise<Result<GambitSearchResultDto[]>> {
    try {
      const response = await this.repository.findGambitResults(locationIds, start, end);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async findMyGambits(start: string | Date, end: string | Date): Promise<Result<GambitSearchResultDto[]>> {
    try {
      const response = await this.repository.findMyGambits(start, end);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getGambits(listId: GamListId, location: GeoCoords): Promise<Result<BaseGambitDto[]>> {
    try {
      const response = await this.repository.getGambits(listId, location);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getGambit(id: string, location?: GeoCoords): Promise<Result<GambitDetailDto>> {
    try {
      const response = await this.repository.getGambit(id, location);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async addNewGambit(formData: GambitForm): Promise<Result<BaseGambitDto>> {
    try {
      const data: GambitFormData = {
        wallStart: this.buildWallDateTime(formData.startDate, formData.startTime),
        wallEnd: this.buildWallDateTime(formData.endDate, formData.endTime),
        locationId: formData.locationId,
        locationPoint: formData.locationPoint,
        address: formData.address,
        chessSetPolicy: formData.chessSetPolicy,
        ownerChessSetCount: formData.ownerChessSetCount,
        clubId: formData.clubId !== 'user' ? formData.clubId : null,
        gambitType: formData.gambitType,
        gambitCategory: formData.gambitCategory,
        title: formData.title,
        description: formData.description,
        timed: formData.timed,
        streamed: formData.streamed,
        private: formData.private,
        repeat: formData.repeat,
        repeatUntil: formData.repeatUntil
          ? this.buildWallDateTime(formData.repeatUntil, formData.startTime)
          : undefined,
        isChallenge: formData.isChallenge,
        invitationUserIds: formData.invitationUserIds,
      };
      const response = await this.repository.addNewGambit(data);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createFirestoreDoc(id: string): Promise<Result<any>> {
    try {
      const response = await this.repository.createFirestoreDoc(id);
      return response?.data ? response : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateGambit(
    id: string,
    formData: GambitForm,
    options?: GambitRecurrenceOptions,
  ): Promise<Result<BaseGambitDto | { status: number }>> {
    try {
      const data: GambitFormData = {
        wallStart: this.buildWallDateTime(formData.startDate, formData.startTime),
        wallEnd: this.buildWallDateTime(formData.endDate, formData.endTime),
        locationId: formData.locationId,
        locationPoint: formData.locationPoint,
        address: formData.address,
        chessSetPolicy: formData.chessSetPolicy,
        clubId: formData.clubId !== 'user' ? formData.clubId : null,
        gambitType: formData.gambitType,
        gambitCategory: formData.gambitCategory,
        title: formData.title,
        description: formData.description,
        timed: formData.timed,
        streamed: formData.streamed,
        private: formData.private,
        repeat: formData.repeat,
      };

      const response = await this.repository.updateGambit(id, data, options);
      return response?.data ? response : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateChallenge(id: string, formData: GambitForm): Promise<Result<{ message: string; gambitId?: string }>> {
    try {
      const data: Partial<GambitFormData> = {
        wallStart: this.buildWallDateTime(formData.startDate, formData.startTime),
        wallEnd: this.buildWallDateTime(formData.endDate, formData.endTime),
        locationId: formData.locationId,
        locationPoint: formData.locationPoint,
        address: formData.address,
        gambitType: formData.gambitType,
        title: formData.title,
        description: formData.description,
        timed: formData.timed,
        streamed: formData.streamed,
      };

      const response = await this.repository.updateChallenge(id, data);

      return response?.data ? response : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deleteGambit(id: string, options?: GambitRecurrenceOptions): Promise<Result<BaseGambitDto> | null> {
    try {
      const response = await this.repository.deleteGambit(id, options);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async joinSpark(data: SparkGambitOptions) {
    try {
      const response = await this.repository.joinSpark(data);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createAttendance(
    gambitId: string,
    options?: { recurrences?: boolean },
  ): Promise<Result<BaseAttendanceDto> | null> {
    try {
      const response = await this.repository.createAttendance(gambitId, options);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateAttendance(
    gambitId: string,
    chessSetCount: number,
    status: GambitAttendanceStatus = GambitAttendanceStatus.CONFIRMED,
  ): Promise<Result<BaseAttendanceDto> | null> {
    try {
      const response = await this.repository.updateAttendance(gambitId, chessSetCount, status);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deleteAttendance(
    gambitId: string,
    options?: { recurrences?: boolean },
  ): Promise<Result<BaseAttendanceDto> | null> {
    try {
      const response = await this.repository.deleteAttendance(gambitId, options);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createInvitations(gambitId: string, userIds: string[], type: GambitInvitationType): Promise<Result<any>> {
    try {
      const response = await this.repository.createInvitations(gambitId, userIds, type);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async acceptChallenge(gambitId: string): Promise<Result<{ gambitId: string }>> {
    try {
      const response = await this.repository.acceptChallenge(gambitId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async declineInvitation(gambitId: string): Promise<Result<any>> {
    try {
      const response = await this.repository.declineInvitation(gambitId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async withdrawGambitInvitation(gambitId: string): Promise<Result<any>> {
    try {
      const response = await this.repository.withdrawChallenge(gambitId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getRespondInvitations(): Promise<Result<RespondGambitDto[]>> {
    try {
      const response = await this.repository.getRespondInvitations();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getOutgoingInvitations(userId: string): Promise<Result<OutgoingInvitationDto[]>> {
    try {
      const response = await this.repository.getOutgoingInvitations(userId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getLiveGambits(): Promise<Result<LiveGambitDto[]>> {
    try {
      const response = await this.repository.getLiveGambits();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async dismissRecentGambit(gambitId: string): Promise<Result<boolean>> {
    try {
      const response = await this.repository.dismissRecentGambit(gambitId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async cancelGambitInvitation(gambitId: string): Promise<Result<any>> {
    try {
      const response = await this.repository.cancelGambitInvitation(gambitId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }
}
